/**
 * @file validation.tsx
 *
 * @fileoverview Helpers for field and field group validation for entity forms.
 */
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { FieldGroupInfo } from '~/jobiqo/models/forms';
import { EntityDefinition, EntityField } from '~/jobiqo/models/entity';
import { filterExcludedFields } from './fields';
import { getYupMultipleFilesValidation } from '~/jobiqo/components/Form/Widgets/File';
import { getCommonTranslations } from '~/jobiqo/utils/intl';

/**
 * Generate schema.
 *
 * Generates a validation schema for the form using yup.
 *
 * @param {any} schema
 *   The schema for form validation.
 */
export const generateValidationSchema = (
  fieldGroups: FieldGroupInfo[],
  excludedFieldList: string[],
  definition: EntityDefinition,
  extraValidationSchema: any,
) => {
  const intl = useIntl();
  const {
    EMAIL_VALIDATION,
    FIELD_REQUIRED,
    FIELD_NUMBER,
    FIELD_NUMBER_POSITIVE,
  } = getCommonTranslations();
  let shape = {};
  // validate required fields.
  filterExcludedFields(
    fieldGroups,
    excludedFieldList,
    definition.fields,
    definition,
  ).forEach((field) => {
    const allowedTextLength = getAllowedTextLength(field);

    if (allowedTextLength) {
      shape = {
        ...shape,
        [field.id]: (shape[field.id] ?? Yup.string()).max(
          allowedTextLength,
          intl.formatMessage(
            {
              id: 'max_characters_limit',
              defaultMessage: 'Maximum {allowedTextLength} characters',
            },
            { allowedTextLength },
          ),
        ),
      };
    }

    if (field.required) {
      shape = {
        ...shape,
        [field.id]: (shape[field.id] ?? Yup.string()).required(FIELD_REQUIRED),
      };
    }

    if (field.type === 'email') {
      shape = {
        ...shape,
        [field.id]: (shape[field.id] ?? Yup.string())
          .nullable()
          .email(EMAIL_VALIDATION),
      };
    }

    if (field.type === 'link') {
      shape = {
        ...shape,
        [field.id]: (shape[field.id] ?? Yup.string()).url(
          intl.formatMessage({
            id: 'url_validation',
            defaultMessage: 'Field must be formatted as valid url',
          }),
        ),
      };
    }

    if (field.type === 'address' && !field.useGeocompleteWidget) {
      const addressRequiredFields = [
        'addressLine1',
        'postalCode',
        'countryCode',
        'locality',
      ];

      let objectValidationParts = {};
      addressRequiredFields.forEach((addressRequiredField) => {
        objectValidationParts = {
          ...objectValidationParts,
          [addressRequiredField]: Yup.string().required(FIELD_REQUIRED),
        };
      });

      // Special validation for administrativeArea (because only exists in some countries).
      // Only apply a validation if the field is set as required.
      const administrativeAreaFieldOverrideSetting = definition?.fields
        .filter((field) => field?.id === 'address')?.[0]
        ?.settings?.filter(
          (fieldSettings) => fieldSettings?.key === 'field_overrides',
        )?.[0].value?.administrativeArea?.override;

      if (administrativeAreaFieldOverrideSetting === 'required') {
        objectValidationParts = {
          ...objectValidationParts,
          administrativeArea: Yup.string()
            .nullable()
            .notOneOf([undefined], FIELD_REQUIRED),
        };
      }

      shape = {
        ...shape,
        [field.id]: Yup.object().shape(objectValidationParts),
      };
    }

    if (field.type === 'file') {
      shape = {
        ...shape,
        [field.id]: getYupMultipleFilesValidation(
          Yup,
          intl,
          field.settings.find((setting) => setting.key === 'max_filesize')
            ?.value,
          field.maxNumItems,
          field.required,
        ),
      };
    }

    // Validation for Salary range free input field widget.
    // As we use input type "text" but we except only numbers we need to transform
    // and validate various use cases.
    if (field.type === 'jobiqo_job_salary_range') {
      let salaryWidgetValidationParts = {
        ['min_salary']: Yup.string()
          .nullable()
          .optional()
          .test('valid-format', FIELD_NUMBER, (value) => {
            if (!value) {
              return true;
            }
            try {
              const parsedNumber = parseLocaleNumber(value, intl.locale);
              return !isNaN(parsedNumber);
            } catch (e) {
              return false;
            }
          })
          .test('is-positive', FIELD_NUMBER_POSITIVE, (value) => {
            if (!value) {
              return true;
            }
            const parsedNumber = parseLocaleNumber(value, intl.locale);
            return parsedNumber > 0;
          }),
        ['max_salary']: Yup.string()
          .nullable()
          .optional()
          .test('valid-format', FIELD_NUMBER, (value) => {
            if (!value) {
              return true;
            }
            try {
              const parsedNumber = parseLocaleNumber(value, intl.locale);
              return !isNaN(parsedNumber);
            } catch (e) {
              return false;
            }
          })
          .test('is-positive', FIELD_NUMBER_POSITIVE, (value) => {
            if (!value) {
              return true;
            }
            const parsedNumber = parseLocaleNumber(value, intl.locale);
            return parsedNumber > 0;
          }),
      };

      shape = {
        ...shape,
        [field.id]: Yup.object().shape(salaryWidgetValidationParts),
      };
    }
  });

  // Add extra validation passed from the outside as a prop.
  if (extraValidationSchema) {
    shape = {
      ...shape,
      ...extraValidationSchema,
    };
  }

  return Yup.object().shape(shape);
};

/**
 * Gets the allowed text length for a field.
 * @param field The entity field.
 * @returns The allowed text length.
 */
export function getAllowedTextLength(field: EntityField) {
  const DEFAULT_ALLOWED_TEXT_LENGTH = 0;

  // We currently only support the field_job_description field limit.
  if (field.id !== 'field_job_description') {
    return DEFAULT_ALLOWED_TEXT_LENGTH;
  }

  return (
    // For the field_job_description field, we only support the restricted_html format.
    field.allowedFormatsMaxTextLength.find(
      (format) => format.key === 'restricted_html',
    )?.value ?? DEFAULT_ALLOWED_TEXT_LENGTH
  );
}

/**
 * Parse a locale number. Use for the salary range field
 * widget where the number is stored as a string we need to parse it using the
 * locale set in app.js (see getIntlLocale).
 *
 * @param stringNumber
 *  The string number to parse.
 * @param locale
 *  The locale to use.
 * @returns
 *  The parsed number.
 */
function parseLocaleNumber(stringNumber: string, locale: string) {
  const example = new Intl.NumberFormat(locale).format(1.1);
  const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, 'g');
  const cleaned = stringNumber.replace(cleanPattern, '');
  const normalized = cleaned.replace(example.charAt(1), '.');
  return parseFloat(normalized);
}
